import { Moment } from 'moment';

// Modèles
import { ModelBase } from '../../abstract/model-base.model';
import { BankAccount } from '../bank-accounts/bank-account.model';
import { BankStatementAutomation } from '../config/bank-statement-automation.model';
import { BankStatementLine } from './bank-statement-line.model';
import { Currency } from '../../core/currency.model';

export enum BankStatementPipeline {
    OcrOnly             = 'ocr_only',
    Reconciliation      = 'reconciliation',
    TransactionsImport  = 'transactions_import',
}

export enum BankStatementProcessingStatus {
    Uploaded = 'uploaded',
    Analyzing = 'analyzing',
    AnalysisFailed = 'analysis_failed',
    OcrInProgress = 'ocr_in_progress',
    OcrFailed = 'ocr_failed',
    OcrDone = 'ocr_done',
    ConsistencyWarning = 'consistency_warning',
    MatchingInProgress = 'matching_in_progress',
    PendingReconciliation = 'pending_reconciliation',
    ImportInProgress = 'import_in_progress',
    Imported = 'imported',
    ReconciliationDone = 'reconciliation_done',
}

/** Statuts pour lesquels le pipeline tourne en arrière-plan → polling actif */
export const BANK_STATEMENT_PROCESSING_STATUSES: BankStatementProcessingStatus[] = [
    BankStatementProcessingStatus.Analyzing,
    BankStatementProcessingStatus.OcrInProgress,
    BankStatementProcessingStatus.MatchingInProgress,
    BankStatementProcessingStatus.ImportInProgress,
];

export class BankStatement extends ModelBase {

    static override CLASS_NAME: string = 'BankStatement';

    /*----------------------------------------------------------------------------------------
     * Propriétés
     ----------------------------------------------------------------------------------------*/

    private _balanceDifference!: number;
    private _bankAccount!: BankAccount;
    private _biiforClosingBalance!: number;
    private _biiforOpeningBalance!: number;
    private _biiforTotalCredit!: number;
    private _biiforTotalDebit!: number;
    private _closingBalance!: number;
    private _closingBalanceBac!: number;
    private _currency!: Currency;
    private _flowDifference!: number;
    private _isBalanced!: boolean;
    private _isValid!: boolean;
    private _lines!: BankStatementLine[];
    private _matchedLinesCount!: number;
    private _openingBalance!: number;
    private _openingBalanceBac!: number;
    private _periodEnd!: Moment;
    private _periodStart!: Moment;
    private _pipeline!: string;
    private _processingStatus!: BankStatementProcessingStatus;
    private _processingStatusReason!: string;
    private _reference!: string;
    private _statementAutomation!: BankStatementAutomation;
    private _totalCredit!: number;
    private _totalCreditBac!: number;
    private _totalDebit!: number;
    private _totalDebitBac!: number;
    private _totalLinesCount!: number;
    private _totalOperationsCredit!: number;
    private _totalOperationsCreditBac!: number;
    private _totalOperationsDebit!: number;
    private _totalOperationsDebitBac!: number;

    /*----------------------------------------------------------------------------------------
     * Publiques
     ----------------------------------------------------------------------------------------*/

    public override toString(): string {
        const hasReference = this.reference ? ` ${this.reference}` : '';
        const hasDates = this.periodStart && this.periodEnd;
        const dateRange = hasDates ? ` du ${this.periodStart.format('DD/MM/YYYY')} au ${this.periodEnd.format('DD/MM/YYYY')}` : '';
        return `Relevé bancaire${hasReference}${dateRange}`;
    }

    /*----------------------------------------------------------------------------------------
     * Accesseurs
     ----------------------------------------------------------------------------------------*/

    public get balanceDifference(): number {
        return this._balanceDifference;
    }

    public set balanceDifference(value: any) {
        this._setValue('_balanceDifference', value, 'float');
    }

    public get bankAccount(): BankAccount {
        return this._bankAccount;
    }

    public set bankAccount(value: any) {
        this._setValue('_bankAccount', value, 'BankAccount');
    }

    public get closingBalance(): number {
        return this._closingBalance;
    }

    public set closingBalance(value: any) {
        this._setValue('_closingBalance', value, 'float');
    }

    public get closingBalanceBac(): number {
        return this._closingBalanceBac;
    }

    public set closingBalanceBac(value: any) {
        this._setValue('_closingBalanceBac', value, 'float');
    }

    public get currency(): Currency {
        return this._currency;
    }

    public set currency(value: any) {
        this._setValue('_currency', value, 'Currency');
    }

    public get flowDifference(): number {
        return this._flowDifference;
    }

    public set flowDifference(value: any) {
        this._setValue('_flowDifference', value, 'float');
    }

    public get isBalanced(): boolean {
        return this._isBalanced;
    }

    public set isBalanced(value: any) {
        this._setValue('_isBalanced', value, 'boolean');
    }

    public get isValid(): boolean {
        return this._isValid;
    }

    public set isValid(value: any) {
        this._setValue('_isValid', value, 'boolean');
    }

    public get lines(): BankStatementLine[] {
        return this._lines;
    }

    public set lines(value: any) {
        this._setValue('_lines', value, 'BankStatementLine');
    }

    public get openingBalance(): number {
        return this._openingBalance;
    }

    public set openingBalance(value: any) {
        this._setValue('_openingBalance', value, 'float');
    }

    public get openingBalanceBac(): number {
        return this._openingBalanceBac;
    }

    public set openingBalanceBac(value: any) {
        this._setValue('_openingBalanceBac', value, 'float');
    }

    public get periodEnd(): Moment {
        return this._periodEnd;
    }

    public set periodEnd(value: any) {
        this._setValue('_periodEnd', value, 'date');
    }

    public get periodStart(): Moment {
        return this._periodStart;
    }

    public set periodStart(value: any) {
        this._setValue('_periodStart', value, 'date');
    }

    public get reference(): string {
        return this._reference;
    }

    public set reference(value: any) {
        this._setValue('_reference', value, 'string');
    }

    public get statementAutomation(): BankStatementAutomation {
        return this._statementAutomation;
    }

    public set statementAutomation(value: any) {
        this._setValue('_statementAutomation', value, 'BankStatementAutomation');
    }

    public get totalCredit(): number {
        return this._totalCredit;
    }

    public set totalCredit(value: any) {
        this._setValue('_totalCredit', value, 'float');
    }

    public get totalCreditBac(): number {
        return this._totalCreditBac;
    }

    public set totalCreditBac(value: any) {
        this._setValue('_totalCreditBac', value, 'float');
    }

    public get totalDebit(): number {
        return this._totalDebit;
    }

    public set totalDebit(value: any) {
        this._setValue('_totalDebit', value, 'float');
    }

    public get totalDebitBac(): number {
        return this._totalDebitBac;
    }

    public set totalDebitBac(value: any) {
        this._setValue('_totalDebitBac', value, 'float');
    }

    public get totalOperationsCredit(): number {
        return this._totalOperationsCredit;
    }

    public set totalOperationsCredit(value: any) {
        this._setValue('_totalOperationsCredit', value, 'float');
    }

    public get totalOperationsCreditBac(): number {
        return this._totalOperationsCreditBac;
    }

    public set totalOperationsCreditBac(value: any) {
        this._setValue('_totalOperationsCreditBac', value, 'float');
    }

    public get totalOperationsDebit(): number {
        return this._totalOperationsDebit;
    }

    public set totalOperationsDebit(value: any) {
        this._setValue('_totalOperationsDebit', value, 'float');
    }

    public get totalOperationsDebitBac(): number {
        return this._totalOperationsDebitBac;
    }

    public set totalOperationsDebitBac(value: any) {
        this._setValue('_totalOperationsDebitBac', value, 'float');
    }

    public get biiforClosingBalance(): number {
        return this._biiforClosingBalance;
    }

    public set biiforClosingBalance(value: any) {
        this._setValue('_biiforClosingBalance', value, 'float');
    }

    public get biiforOpeningBalance(): number {
        return this._biiforOpeningBalance;
    }

    public set biiforOpeningBalance(value: any) {
        this._setValue('_biiforOpeningBalance', value, 'float');
    }

    public get biiforTotalCredit(): number {
        return this._biiforTotalCredit;
    }

    public set biiforTotalCredit(value: any) {
        this._setValue('_biiforTotalCredit', value, 'float');
    }

    public get biiforTotalDebit(): number {
        return this._biiforTotalDebit;
    }

    public set biiforTotalDebit(value: any) {
        this._setValue('_biiforTotalDebit', value, 'float');
    }

    public get matchedLinesCount(): number {
        return this._matchedLinesCount;
    }

    public set matchedLinesCount(value: any) {
        this._setValue('_matchedLinesCount', value, 'integer');
    }

    public get pipeline(): string {
        return this._pipeline;
    }

    public set pipeline(value: any) {
        this._setValue('_pipeline', value, 'string');
    }

    public get processingStatus(): BankStatementProcessingStatus {
        return this._processingStatus;
    }

    public set processingStatus(value: any) {
        this._setValue('_processingStatus', value, 'string');
    }

    public get processingStatusReason(): string {
        return this._processingStatusReason;
    }

    public set processingStatusReason(value: any) {
        this._setValue('_processingStatusReason', value, 'string');
    }

    public get totalLinesCount(): number {
        return this._totalLinesCount;
    }

    public set totalLinesCount(value: any) {
        this._setValue('_totalLinesCount', value, 'integer');
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques — helpers
     ----------------------------------------------------------------------------------------*/

    /** Vrai si le pipeline tourne en arrière-plan */
    public get isProcessing(): boolean {
        return BANK_STATEMENT_PROCESSING_STATUSES.includes(this._processingStatus);
    }

    /** Écart entre solde de clôture PDF et solde de clôture Biifor */
    public get closingBalanceDelta(): number | null {
        if (this._closingBalance == null || this._biiforClosingBalance == null) return null;
        return this._closingBalance - this._biiforClosingBalance;
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    /*----------------------------------------------------------------------------------------
     * Méthodes protégées
     ----------------------------------------------------------------------------------------*/

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/
}